const MAX_BUFFERED_INPUT_EVENTS = 120;

const CONTROLLER_BUTTON_NAMES = {
  0: 'bottom',
  1: 'right',
  2: 'left',
  3: 'top',
  4: 'left_bumper',
  5: 'right_bumper',
  9: 'start',
  12: 'dpad_up',
  13: 'dpad_down',
  14: 'dpad_left',
  15: 'dpad_right'
};

const getTelemetryState = () => {
  if (typeof window === 'undefined') return null;

  if (!window.__clInputTelemetry) {
    window.__clInputTelemetry = {
      startedAt: Date.now(),
      events: [],
      dropped: 0
    };
  }

  return window.__clInputTelemetry;
};

const roundNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number) : undefined;
};

const cleanString = (value, maxLength = 80) => {
  if (value === undefined || value === null) return undefined;
  return String(value).slice(0, maxLength);
};

const compactObject = object =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined)
  );

const parseMetadataObject = metadata => {
  if (!metadata) return {};
  if (typeof metadata === 'object') return { ...metadata };

  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

const getSceneKey = scene => scene?.scene?.key;

const getLevelId = scene =>
  scene?.dataIndex ??
  scene?.currentLevelID ??
  scene?.levelID ??
  scene?.levelData?.id ??
  scene?.levelData?.index;

const getMap = scene => scene?.map || scene?.levelScene?.map;

const getWorldPointFromPointer = (scene, pointer) => {
  if (!pointer) return null;

  try {
    if (
      typeof pointer.positionToCamera === 'function' &&
      scene?.cameras?.main
    ) {
      return pointer.positionToCamera(scene.cameras.main);
    }
  } catch (e) {
    return null;
  }

  if (Number.isFinite(Number(pointer.worldX))) {
    return { x: pointer.worldX, y: pointer.worldY };
  }

  return null;
};

const getTilePoint = (scene, worldPoint) => {
  const map = getMap(scene);
  if (!map || !worldPoint) return null;

  try {
    return {
      x: map.worldToTileX(worldPoint.x),
      y: map.worldToTileY(worldPoint.y)
    };
  } catch (e) {
    return null;
  }
};

const getCursorWorldPoint = scene => {
  const cursor = scene?.cursorPointer || scene?.levelScene?.cursorPointer;
  if (!cursor) return null;

  return {
    x: cursor.x,
    y: cursor.y
  };
};

const getInputContext = () => {
  if (typeof window === 'undefined') return null;

  const activeScenes = window.game?.scene?.getScenes?.(true) || [];
  const controllerActive =
    window.game?.controllerInput === true ||
    activeScenes.some(scene => scene?.game?.controllerInput === true);
  const controllerConnected = activeScenes.some(scene =>
    scene?.input?.gamepad
      ?.getAll?.()
      ?.some(pad => pad && pad.connected !== false)
  );

  return compactObject({
    device: controllerActive ? 'controller' : 'pointer',
    controller_active: controllerActive,
    controller_connected: controllerConnected
  });
};

const buildBaseEvent = (scene, entry = {}) => {
  const state = getTelemetryState();
  const now = Date.now();

  return compactObject({
    t: now,
    ms: state ? now - state.startedAt : undefined,
    scene: cleanString(entry.scene || getSceneKey(scene)),
    level_id: cleanString(entry.level_id ?? getLevelId(scene), 64),
    action: cleanString(entry.action),
    input: cleanString(entry.input),
    controller_active: scene?.game?.controllerInput === true,
    ...entry
  });
};

export const recordInputTelemetry = (scene, entry = {}) => {
  const state = getTelemetryState();
  if (!state) return;

  const event = buildBaseEvent(scene, entry);
  if (!event.action && !event.input) return;

  if (state.events.length >= MAX_BUFFERED_INPUT_EVENTS) {
    state.events.shift();
    state.dropped += 1;
  }

  state.events.push(event);
};

export const recordPointerTelemetry = (scene, pointer, entry = {}) => {
  const worldPoint = getWorldPointFromPointer(scene, pointer);
  const tilePoint = getTilePoint(scene, worldPoint);
  const pointerType =
    pointer?.pointerType || (pointer?.wasTouch ? 'touch' : 'mouse');
  const details = compactObject(entry);

  recordInputTelemetry(scene, {
    ...details,
    input: details.input || pointerType,
    action: details.action || 'pointer',
    screen_x: roundNumber(pointer?.x ?? details.screen_x),
    screen_y: roundNumber(pointer?.y ?? details.screen_y),
    world_x: roundNumber(worldPoint?.x ?? details.world_x),
    world_y: roundNumber(worldPoint?.y ?? details.world_y),
    tile_x: roundNumber(tilePoint?.x ?? details.tile_x),
    tile_y: roundNumber(tilePoint?.y ?? details.tile_y),
    button: roundNumber(pointer?.button)
  });
};

export const recordControllerTelemetry = (scene, button, entry = {}) => {
  const buttonIndex =
    typeof button === 'object' && button !== null ? button.index : button;
  const worldPoint = getCursorWorldPoint(scene);
  const tilePoint = getTilePoint(scene, worldPoint);
  const details = compactObject(entry);

  recordInputTelemetry(scene, {
    ...details,
    input: 'controller',
    action: details.action || 'controller_button',
    button: roundNumber(buttonIndex),
    button_name: cleanString(CONTROLLER_BUTTON_NAMES[buttonIndex]),
    world_x: roundNumber(worldPoint?.x),
    world_y: roundNumber(worldPoint?.y),
    tile_x: roundNumber(tilePoint?.x),
    tile_y: roundNumber(tilePoint?.y)
  });
};

export const drainInputTelemetry = () => {
  const state = getTelemetryState();
  if (!state) return { events: [], dropped: 0 };

  const events = state.events.slice();
  const dropped = state.dropped;
  state.events = [];
  state.dropped = 0;

  return { events, dropped };
};

export const appendInputTelemetryToMetadata = metadata => {
  const nextMetadata = parseMetadataObject(metadata);
  const inputContext = getInputContext();
  const { events, dropped } = drainInputTelemetry();

  if (inputContext) {
    nextMetadata.input_context = inputContext;
  }
  if (events.length) {
    nextMetadata.input_events = events;
    nextMetadata.input_event_count = events.length;
  }
  if (dropped) {
    nextMetadata.input_events_dropped = dropped;
  }

  return nextMetadata;
};
